import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import ClientOnlyPortal from '@chegg-wt/core/components/ClientOnlyPortal'
import { AppState } from '@chegg-wt/core/state/types'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import { REDESIGNED_UNLOCK_MODAL } from '@chegg-wt/core/state/modals/constants'
import { modalDataSelector } from '@chegg-wt/core/state/modals/selectors'
import { closeModal, unlockActivityCompleted } from '@chegg-wt/core/state/modals/actions'
import {
  writingFormSubmit,
  formSubmitStarted,
  formSubmitStopped,
} from '@chegg-wt/core/state/form/actions'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { UnlockCompletePayload } from '@chegg-wt/core/state/modals/types'
import { UnlockCompleteActions } from '@chegg-wt/core/components/Modals/UnlockAccountGateModal/types'
import { Config } from '@chegg-wt/core/config'
import usePriceTest from '@chegg-wt/core/hooks/usePriceTest'
import { trackWithoutThrow } from '@chegg-wt/core/utils/errors'
import {
  emitComponentViewEvent,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import { componentOrigins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import { EB_AD_UNLOCK_MODAL } from '@chegg-wt/core/state/optimizely/constants'
import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import {
  adUnlockVariationSelector,
  authModalExpFlagSelector,
} from '@chegg-wt/core/state/createCitation/selectors'
import { AdUnlockVariations } from '@chegg-wt/core/types/core'
import { startAuthFlow } from '@chegg-wt/core/state/user/actions'
import { AuthPayload } from '@chegg-wt/core/types/auth'
import { useRoutesContext } from '../RoutesContext'
import AppLink from '../AppLink'
import { AuthModalOrigin } from '../Modals/AuthModal/types'
import {
  FOOTER_BANNER_CTA_TEXT,
  FOOTER_BANNER_TEXT,
  UCF_UNLOCK_MODAL_ID,
  SPONSORED_CTA_TEXT,
  SUBSCRIBE_CTA_TEXT,
  UNLOCK_MODAL_TITLE,
  TRY_IT_FOR_TODAY_CTA_TEXT,
  UNLOCK_AD_MODAL_SUBHEADING,
  CREATE_A_FREE_ACCOUNT,
  YOU_HAVE_HIT_YOUR_CITATION_LIMIT,
  UNLIMITED_CITATIONS,
} from './constants'
import {
  HackyModal,
  ModalContents,
  SponsoredCTA,
  SubscribeCTA,
  HackyFooter,
  UnlockModalSubHeading,
  UnlimitedCitationsSubHeading,
} from './styled'
import { SmallNotificationBanner } from './SmallNotificationBanner'
import {
  CREATE_A_FREE_ACCOUNT_DATA_TEST,
  SPONSORED_CTA_DATA_TEST,
  SUBSCRIBE_CTA_DATA_TEST,
} from './testIds'

interface UCFUnlockModalProps {
  isLoggedIn: boolean
  authModalExpFlag: boolean
  adUnlockModalExpVariant: AdUnlockVariations
  modalData: {
    withAuthBanner: boolean
    alwaysParticipate?: boolean
  }
  closeModal: (name: string, origin?: string) => void
  writingFormSubmit: ({ citation, initialCitationData }) => void
  formSubmitStopped: () => void
  clickSponsoredRioEvent: () => void
  clickCreateFreeAccountRioEvent: () => void
  clickTryItForFreeTodayRioEvent: () => void
  clickSubscribeRioEvent: (value: string) => void
  clickSignInRioEvent: () => void
  modalOpenRioEvent: () => void
  unlockActivityCompleted: (payload: UnlockCompletePayload) => void
  formSubmitStarted: () => void
  startAuthFlow: (payload: AuthPayload) => void
}

const MODAL_ANIMATION_DURATION_MS = 300

export const UCFUnlockModal: React.FC<UCFUnlockModalProps> = ({
  isLoggedIn,

  closeModal,
  modalData,
  unlockActivityCompleted,
  formSubmitStopped,
  clickSponsoredRioEvent,
  clickCreateFreeAccountRioEvent,
  clickTryItForFreeTodayRioEvent,
  clickSubscribeRioEvent,
  clickSignInRioEvent,
  modalOpenRioEvent,
  formSubmitStarted,
  authModalExpFlag,
  adUnlockModalExpVariant,
  startAuthFlow,
}) => {
  const showAuthModal = authModalExpFlag && !isLoggedIn
  const showAdUnlockFirstVariant =
    adUnlockModalExpVariant === AdUnlockVariations.VARIANT1 && !isLoggedIn
  const showAdUnlockSecondVariant =
    adUnlockModalExpVariant === AdUnlockVariations.VARIANT2 && !isLoggedIn
  const { isEnabled: staticUnlockModalEnabled, clientReady } = useSafeOptimizelyFeature(
    EB_AD_UNLOCK_MODAL
  )

  const { siteName } = Config
  const staticUnlockModalEnabledOnEB =
    clientReady && staticUnlockModalEnabled && siteName === 'EasyBib'
  const { toUpgrade } = useRoutesContext()
  const [isOpen, setIsOpen] = useState(true)
  const [isSAModalOpen, setIsSAModalOpen] = useState(false)
  const [isAdActivityOpen, setIsAdActivityOpen] = useState(false)
  const { price } = usePriceTest()
  const SUBSCRIBE_CTA_TEXT_STATIC =
    staticUnlockModalEnabledOnEB || showAdUnlockFirstVariant
      ? TRY_IT_FOR_TODAY_CTA_TEXT
      : SUBSCRIBE_CTA_TEXT(price)
  const backdropClick = () => {
    setIsOpen(false)
  }

  const onSponsoredClick = () => {
    clickSponsoredRioEvent()

    setIsOpen(false)
    setIsAdActivityOpen(true)

    new Promise((resolve) => {
      setTimeout(resolve, MODAL_ANIMATION_DURATION_MS)
    }).then(() =>
      window.SBMGlobal.run
        .showUnlock({ skipStartView: true })
        .catch(trackWithoutThrow)
        .then(() =>
          unlockActivityCompleted({
            completionAction: UnlockCompleteActions.FORM_SUBMIT,
          })
        )
    )
  }

  const onSignInClick = () => {
    formSubmitStarted()
    clickSignInRioEvent()

    setIsOpen(false)
    setIsSAModalOpen(true)
  }

  const onSubscribeClick = () => {
    if (SUBSCRIBE_CTA_TEXT_STATIC === TRY_IT_FOR_TODAY_CTA_TEXT) {
      clickTryItForFreeTodayRioEvent()
    } else clickSubscribeRioEvent(SUBSCRIBE_CTA_TEXT_STATIC)
    if (showAuthModal) {
      startAuthFlow({
        defaultFlow: 'signup',
        origin: AuthModalOrigin.UCF_UNBLOCK_MODAL,
        redirectTo: toUpgrade(),
      })
    }
  }

  const onSubscribeClickFirstVariant = () => {
    clickTryItForFreeTodayRioEvent()
    startAuthFlow({
      defaultFlow: 'signup',
      origin: AuthModalOrigin.UCF_UNBLOCK_MODAL,
      redirectTo: toUpgrade(),
    })
  }

  const onCreateFreeAccountClick = () => {
    clickCreateFreeAccountRioEvent()
    startAuthFlow({
      defaultFlow: 'signup',
      origin: AuthModalOrigin.CITATION_FORM_PAGE,
    })
  }

  const secondAdUnlockVariant = () => (
    <ModalContents staticUnlockModalEnabledOnEB={staticUnlockModalEnabledOnEB}>
      <UnlimitedCitationsSubHeading>{UNLIMITED_CITATIONS}</UnlimitedCitationsSubHeading>
      <SponsoredCTA
        staticUnlockModalEnabledOnEB={staticUnlockModalEnabledOnEB}
        onClick={onCreateFreeAccountClick}
        data-test={CREATE_A_FREE_ACCOUNT_DATA_TEST}
        isAdUnlockVariant={showAdUnlockFirstVariant || showAdUnlockSecondVariant}
      >
        {CREATE_A_FREE_ACCOUNT}
      </SponsoredCTA>
    </ModalContents>
  )

  const firstAdUnlockVariant = () => (
    <ModalContents staticUnlockModalEnabledOnEB={staticUnlockModalEnabledOnEB}>
      <SponsoredCTA
        staticUnlockModalEnabledOnEB={staticUnlockModalEnabledOnEB}
        onClick={onCreateFreeAccountClick}
        data-test={CREATE_A_FREE_ACCOUNT_DATA_TEST}
        isAdUnlockVariant={showAdUnlockFirstVariant || showAdUnlockSecondVariant}
      >
        {CREATE_A_FREE_ACCOUNT}
      </SponsoredCTA>
      <span>or</span>
      <UnlockModalSubHeading>{UNLOCK_AD_MODAL_SUBHEADING}</UnlockModalSubHeading>
      <SubscribeCTA
        href={undefined}
        data-test={SUBSCRIBE_CTA_DATA_TEST}
        onClick={onSubscribeClickFirstVariant}
      >
        {SUBSCRIBE_CTA_TEXT_STATIC}
      </SubscribeCTA>
    </ModalContents>
  )

  const adUnlockControlVariant = () => (
    <ModalContents staticUnlockModalEnabledOnEB={staticUnlockModalEnabledOnEB}>
      <SponsoredCTA
        staticUnlockModalEnabledOnEB={staticUnlockModalEnabledOnEB}
        onClick={onSponsoredClick}
        data-test={SPONSORED_CTA_DATA_TEST}
        isAdUnlockVariant={showAdUnlockFirstVariant || showAdUnlockSecondVariant}
      >
        {SPONSORED_CTA_TEXT}
      </SponsoredCTA>
      <span>or</span>
      {staticUnlockModalEnabled && (
        <UnlockModalSubHeading>{UNLOCK_AD_MODAL_SUBHEADING}</UnlockModalSubHeading>
      )}
      {showAuthModal ? (
        <SubscribeCTA
          href={undefined}
          data-test={SUBSCRIBE_CTA_DATA_TEST}
          onClick={onSubscribeClick}
        >
          {SUBSCRIBE_CTA_TEXT_STATIC}
        </SubscribeCTA>
      ) : (
        <AppLink route={toUpgrade()}>
          <SubscribeCTA
            href="I'm needed because TS complains otherwise, AppLink replaces me when rendering"
            data-test={SUBSCRIBE_CTA_DATA_TEST}
            onClick={onSubscribeClick}
          >
            {SUBSCRIBE_CTA_TEXT_STATIC}
          </SubscribeCTA>
        </AppLink>
      )}
    </ModalContents>
  )

  const modalContent = () => {
    if (showAdUnlockSecondVariant) return secondAdUnlockVariant()
    if (showAdUnlockFirstVariant) return firstAdUnlockVariant()
    return adUnlockControlVariant()
  }

  useEffect(() => {
    const isContinuing = isSAModalOpen || isAdActivityOpen
    const isBailingOut = !isOpen && !isContinuing
    if (isBailingOut) {
      formSubmitStopped()
    }
  }, [isOpen, isSAModalOpen, isAdActivityOpen])

  useEffect(() => {
    if (!isSAModalOpen) {
      return
    }

    const asyncOpenId = setTimeout(() => {
      startAuthFlow({
        defaultFlow: 'signin',
        origin: showAdUnlockFirstVariant
          ? AuthModalOrigin.CITATION_FORM_PAGE
          : AuthModalOrigin.REDESIGNED_UNLOCK_MODAL,
        alwaysParticipate: modalData.alwaysParticipate,
      })
    }, MODAL_ANIMATION_DURATION_MS)

    return () => {
      clearTimeout(asyncOpenId)
    }
  }, [isSAModalOpen])

  useEffect(() => {
    if (isOpen) {
      modalOpenRioEvent()
      return
    }

    const asyncCloseId = setTimeout(
      () => !isSAModalOpen && closeModal(REDESIGNED_UNLOCK_MODAL),
      MODAL_ANIMATION_DURATION_MS
    )

    return () => {
      clearTimeout(asyncCloseId)
    }
  }, [isOpen])

  return (
    <ClientOnlyPortal>
      <HackyModal
        id={UCF_UNLOCK_MODAL_ID}
        isOpen={isOpen}
        onClose={backdropClick}
        hideCloseButton
        title={
          showAdUnlockSecondVariant
            ? YOU_HAVE_HIT_YOUR_CITATION_LIMIT
            : UNLOCK_MODAL_TITLE
        }
      >
        <>
          {' '}
          {modalContent()}
          {modalData.withAuthBanner && !isLoggedIn && !showAdUnlockSecondVariant ? (
            <HackyFooter>
              <SmallNotificationBanner
                message={FOOTER_BANNER_TEXT}
                ctaText={FOOTER_BANNER_CTA_TEXT}
                onSignInClick={onSignInClick}
              />
            </HackyFooter>
          ) : null}
        </>
      </HackyModal>
    </ClientOnlyPortal>
  )
}

const mapStateToProps = (state: AppState) => ({
  isLoggedIn: loggedInSelector(state),
  modalData: modalDataSelector(state),
  authModalExpFlag: authModalExpFlagSelector(state),
  adUnlockModalExpVariant: adUnlockVariationSelector(state),
})

const mapDispatchToProps = {
  closeModal,
  writingFormSubmit,
  formSubmitStopped,
  unlockActivityCompleted,
  formSubmitStarted,
  clickSponsoredRioEvent: () =>
    userClick({
      origin: origins.unlockModalSponsoredClick,
    }),
  clickCreateFreeAccountRioEvent: () =>
    userClick({
      origin: origins.createFreeAccountAdUnlockModalClick,
    }),
  clickTryItForFreeTodayRioEvent: () =>
    userClick({
      origin: origins.tryItForFreeTodayAdUnlockModalClick,
    }),
  clickSubscribeRioEvent: (value) =>
    userClick({
      origin: origins.unlockModalSubscribeClick,
      value,
    }),
  clickSignInRioEvent: () =>
    userClick({
      origin: origins.unlockModalSignInClick,
    }),
  modalOpenRioEvent: () =>
    emitComponentViewEvent({
      origin: componentOrigins.unlockModalOpen,
    }),
  startAuthFlow,
}

export default connect(mapStateToProps, mapDispatchToProps)(UCFUnlockModal)
